import { get, post, put, del, getActiveInstituteId } from '../lib/apiClient';
import { courseService } from './courseService';

// Map a full question (as returned by GET /questions/{id}) to the PUT request
// body, applying field overrides. Mirrors useQuestionForm.buildPayload so the
// update contract stays identical to the manual-edit modal, while additionally
// preserving fields the modal omits (e.g. the numeric answer). We re-send the
// whole resource so the update is safe whether the backend PUT is a partial
// update or a full replace.
function buildUpdatePayloadFromQuestion(q = {}, changes = {}) {
  const type = q?.questionType || q?.question_type || 'mcq';
  const normalizedType = String(type).toLowerCase();
  const isMcq = normalizedType === 'mcq';
  // Numeric-answer types the backend recognizes. 'integer_type' is the manual
  // form's internal value; the rest are the API's own (INTEGER/NUMERIC/NUMERICAL).
  const isNumeric = ['integer', 'numeric', 'numerical', 'integer_type'].includes(normalizedType);
  // Multiple-correct (MSQ) types (MULTIPLE_CORRECT/MULTI_CORRECT/MSQ). Like MCQ they
  // carry an options list, so a partial edit (difficulty/topic/publish) must re-send
  // both the options and the marking scheme or the backend loses them on the PUT.
  const isMultiple = /multi|msq/.test(normalizedType);
  const hasOptions = isMcq || isMultiple;

  const hasTopicOverride = changes.topicId !== undefined && changes.topicId !== null && changes.topicId !== '';
  const resolvedTopicId = hasTopicOverride
    ? parseInt(changes.topicId)
    : (q?.topicId != null ? parseInt(q.topicId) : null);

  const difficultyLevel = String(
    changes.difficultyLevel || q?.difficultyLevel || q?.difficulty_level || 'EASY'
  ).toUpperCase();

  // Publish state. An explicit boolean in `changes` (publish / unpublish action)
  // wins; otherwise preserve whatever the loaded question already had so an
  // unrelated edit (difficulty/topic) never silently flips a question between
  // draft and published.
  const resolvedDraftMode =
    typeof changes.draftMode === 'boolean'
      ? changes.draftMode
      : typeof q?.draftMode === 'boolean'
        ? q.draftMode
        : undefined;

  return {
    text: q?.text ?? q?.question_text ?? '',
    questionImagePath: q?.questionImagePath || '',
    difficultyLevel,
    topicId: resolvedTopicId,
    questionType: type,
    marks: parseInt(q?.marks) || 4,
    negativeMarks: parseFloat(q?.negativeMarks) || 0,
    textFormat: q?.textFormat || 'PLAIN',
    explanation: q?.explanation || '',
    // Preserve tags (comma-separated string) and hints (string[]) on every re-send so
    // an inline/partial edit never silently drops them, whether the backend PUT is a
    // partial update or a full replace. `changes` may override either.
    tags: changes.tags !== undefined ? changes.tags : (q?.tags ?? ''),
    hints: Array.isArray(changes.hints)
      ? changes.hints
      : (Array.isArray(q?.hints) ? q.hints : []),
    ...(typeof resolvedDraftMode === 'boolean' && { draftMode: resolvedDraftMode }),
    ...(q?.instituteId != null && { instituteId: q.instituteId }),
    // Numeric-answer questions (INTEGER/NUMERIC/NUMERICAL) must re-send the answer
    // or the backend rejects the update ("requires a correctAnswer") — this is what
    // breaks publishing a numeric question. The API fields are correctAnswer +
    // answerTolerance; there is no `correctIntegerAnswer` field on the API.
    ...(isNumeric && q?.correctAnswer != null && {
      correctAnswer: q.correctAnswer,
      answerTolerance: q?.answerTolerance ?? 0,
    }),
    // Preserve the marking scheme for multiple-correct questions (backend default is
    // PARTIAL) so an unrelated inline edit never resets how the question is scored.
    ...(isMultiple && { markingScheme: q?.markingScheme || 'PARTIAL' }),
    ...(hasOptions && {
      options: (Array.isArray(q?.options) ? q.options : []).map((opt, i) => ({
        text: opt?.text ?? opt?.option_text ?? '',
        optionImagePath: opt?.optionImagePath || '',
        isCorrect: opt?.isCorrect ?? opt?.is_correct ?? false,
        optionOrder: opt?.optionOrder ?? opt?.option_order ?? i + 1
      }))
    })
  };
}

export const questionService = {
  async searchQuestions(searchParams = {}) {
    try {
      // Sort field/direction. Backend supports sorting by createdAt | updatedAt.
      // Default preserves prior behavior (newest first). We send it both as query
      // params (?sortBy=&sortDirection=) — the form the API documents — and in the
      // body `sorting` object the existing search convention uses, kept in sync so
      // whichever the backend reads, the result is the same and they can't conflict.
      const allowedSortFields = ['createdAt', 'updatedAt'];
      const sortBy = allowedSortFields.includes(searchParams.sortBy) ? searchParams.sortBy : 'createdAt';
      const sortDirection = String(searchParams.sortDirection).toLowerCase() === 'asc' ? 'asc' : 'desc';

      // Prepare search payload for POST /questions/search/advanced
      const payload = {
        criteria: {},
        pagination: {
          page: searchParams.page || 0,
          size: searchParams.size || 20
        },
        sorting: {
          field: sortBy,
          direction: sortDirection
        }
      };

      // instituteId is extracted from the JWT on the backend; no need to include it here.
      if (searchParams.searchText && searchParams.searchText.trim() !== '') {
        payload.criteria.searchText = searchParams.searchText.trim();
      }
      if (searchParams.difficulty && searchParams.difficulty.trim() !== '') {
        payload.criteria.difficultyLevel = searchParams.difficulty.trim();
      }
      if (searchParams.subjectId && searchParams.subjectId !== '') {
        payload.criteria.subjectId = parseInt(searchParams.subjectId);
      }
      if (searchParams.chapterId && searchParams.chapterId !== '') {
        payload.criteria.chapterId = parseInt(searchParams.chapterId);
      }
      if (searchParams.topicId && searchParams.topicId !== '') {
        payload.criteria.topicId = parseInt(searchParams.topicId);
      }
      // Free-text tag filter (QuestionCriteriaDto.tags). The backend matches against
      // the comma-separated tags stored on each question; send the trimmed string.
      if (searchParams.tags && String(searchParams.tags).trim() !== '') {
        payload.criteria.tags = String(searchParams.tags).trim();
      }
      // Draft vs published filter (drives the Question Bank tabs). Only sent when
      // an explicit boolean is provided — omitting it returns both, preserving the
      // old "show everything" behavior for callers that don't pass it.
      if (typeof searchParams.draftMode === 'boolean') {
        payload.criteria.draftMode = searchParams.draftMode;
      }



      const response = await post('/questions/search/advanced', payload, {
        params: { sortBy, sortDirection }
      });
      
      // Parse response. The backend wraps as { message, success, data } and the
      // questions page is { questions: [...], totalCount: N } — note the total is
      // `totalCount` (not totalElements/totalPages), so it must be read explicitly
      // or pagination collapses to a single page.
      const data = response?.data?.data || response?.data || response;
      const questions = data?.content || data?.questions || data || [];
      const totalElements = data?.totalCount ?? data?.totalElements ?? data?.total ?? questions.length;
      const totalPages = data?.totalPages || Math.ceil(totalElements / payload.pagination.size);
      const currentPage = data?.number !== undefined ? data.number : searchParams.page || 0;
      const hasMore = currentPage < totalPages - 1;
      
      return { 
        data: questions,
        pagination: {
          currentPage,
          totalPages,
          totalElements,
          hasMore,
          size: payload.pagination.size
        },
        error: null 
      };
    } catch (error) {
      console.warn('Advanced search failed:', error);
      return { 
        data: [], 
        pagination: {
          currentPage: 0,
          totalPages: 0,
          totalElements: 0,
          hasMore: false,
          size: 20
        },
        // Surface the failure so callers *can* distinguish "no results" from "request
        // failed". data stays [] so the UI still degrades gracefully.
        error: { message: error?.message || 'Failed to search questions' }
      };
    }
  },

  // Kept for backward compatibility — delegates to searchQuestions.
  async getQuestions(filters = {}) {
    return await this.searchQuestions(filters);
  },

  async createQuestion(questionData) {
    try {
      const scopedId = getActiveInstituteId();
      const body = scopedId != null ? { ...questionData, instituteId: scopedId } : { ...questionData };
      // New questions land in draft until a user explicitly publishes them. Default
      // here as a safety net so any create path is draft unless it opted out by
      // passing an explicit draftMode.
      if (typeof body.draftMode !== 'boolean') {
        body.draftMode = true;
      }
      const { data, error, success } = await post('/questions', body);
      if (success) {
        return { data: data?.data || data || null, error: null };
      }
      return { data: null, error: error || { message: 'Failed to create question' } };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to create question' } };
    }
  },

  async updateQuestion(questionId, questionData) {
    try {
      const { data, error, success } = await put(`/questions/${questionId}`, questionData);
      if (success) {
        return { data: data?.data || data || null, error: null };
      }
      return { data: null, error: error || { message: 'Failed to update question' } };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to update question' } };
    }
  },

  // Update only a question's difficulty, topic, and/or draft state without opening
  // the full editor. Re-fetches the complete question first and merges the change
  // in, so no other field (text, options, explanation) is lost — this holds
  // whether the backend PUT is a partial update or a full replace.
  // `changes` = { difficultyLevel?, topicId?, draftMode? }.
  async updateQuestionFields(questionId, changes = {}) {
    try {
      const { data: full, error: loadError } = await this.getQuestionById(questionId);
      if (loadError || !full) {
        return { data: null, error: loadError || { message: 'Failed to load question for update' } };
      }
      const payload = buildUpdatePayloadFromQuestion(full, changes);
      return await this.updateQuestion(questionId, payload);
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to update question' } };
    }
  },

  // Move a question between draft and published. `draftMode = false` publishes it,
  // `true` returns it to draft. The backend has no dedicated publish endpoint, so
  // this rides the normal update path (load full question, flip draftMode, PUT).
  async setQuestionDraftMode(questionId, draftMode) {
    return await this.updateQuestionFields(questionId, { draftMode: !!draftMode });
  },

  async getQuestionById(questionId) {
    try {
      const { data, error, success } = await get(`/questions/${questionId}`);
      if (success) {
        return { data: data?.data || data || null, error: null };
      }
      return { data: null, error: error || { message: 'Failed to load question' } };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to load question' } };
    }
  },

  async deleteQuestion(questionId) {
    try {
      const { data, error, success } = await del(`/questions/${questionId}`);
      if (success) {
        return { data: data || null, error: null };
      }
      return { data: null, error: error || { message: 'Failed to delete question' } };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to delete question' } };
    }
  },

  // Delegates to courseService — large page size to populate dropdowns fully.
  async getSubjects(pagination = { page: 0, size: 100 }) {
    return await courseService.getSubjects(null, pagination);
  },

  async getChaptersBySubject(subjectId, pagination = { page: 0, size: 100 }) {
    return await courseService.getChapters(subjectId, pagination);
  },

  async getTopicsByChapter(chapterId, pagination = { page: 0, size: 100 }) {
    return await courseService.getTopics(chapterId, pagination);
  },

  // Return one real topic { code, name } from the current institute, used to
  // pre-fill bulk-upload sample CSVs with a Topic code that actually resolves
  // (the CSV's "Topic ID" column is matched against a topic *code*, not an id).
  // Returns null when the institute has no topics or the fetch fails, so callers
  // can fall back to a placeholder.
  async getSampleTopic() {
    try {
      const { data } = await courseService.getTopics(null, { page: 0, size: 50 });
      const topics = Array.isArray(data) ? data : [];
      const match = topics.find((t) => t?.code);
      return match ? { code: match.code, name: match.name } : null;
    } catch (error) {
      return null;
    }
  },

  // A question only carries topicId/topicName (no subject/chapter), so resolving
  // its full Subject→Chapter→Topic path means walking up: topic -> chapterId,
  // chapter -> subjectId. Used to prefill the inline topic editor.
  async getTopicById(topicId) {
    return await courseService.getTopic(topicId);
  },

  async getChapterById(chapterId) {
    return await courseService.getChapterById(chapterId);
  },

  // Kept for backward compatibility.
  async searchTopics(params = {}) {
    const pagination = { page: 0, size: 100 };
    if (params.chapterId) {
      return await courseService.getTopics(params.chapterId, pagination);
    } else if (params.subjectId) {
      return await courseService.getChapters(params.subjectId, pagination);
    } else {
      return await courseService.getTopics(null, pagination);
    }
  },

  // Upload a question/option image to S3 via the backend.
  // Returns { key, url } — `key` is what gets stored on the question
  // (questionImagePath / optionImagePath), `url` is the public URL for preview.
  async uploadImage(file, topicId, isOption = false) {
    try {
      if (!topicId) {
        return { data: null, error: { message: 'A topic must be selected before uploading an image' } };
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('topicId', topicId);
      formData.append('option', isOption);

      const { data, error, success } = await post('/questions/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (success) {
        const payload = data?.data || data || {};
        return { data: { key: payload.key, url: payload.url }, error: null };
      }
      return { data: null, error: error || { message: 'Failed to upload image' } };
    } catch (error) {
      return { data: null, error: { message: error?.response?.data?.message || error?.message || 'Failed to upload image' } };
    }
  },

  // Bulk-upload a CSV of questions. `endpoint` selects the parser on the backend:
  //   '/questions/bulk-upload'          → standard MCQ questions (default)
  //   '/questions/bulk-upload/numeric'  → numeric/integer-answer questions
  // Each supported format is declared in question-bank/questionUploadTypes.js;
  // callers pass that type's `endpoint`. Defaulting to the MCQ endpoint keeps
  // existing callers behaving exactly as before.
  async bulkUploadQuestions(file, endpoint = '/questions/bulk-upload') {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Attach X-Institute-Id explicitly (not only via the interceptor) so the upload
      // doesn't depend on the interceptor's runtime conditions. SUPER_ADMIN → switcher's
      // selected institute; other roles (scopedId null) fall back to their JWT institute.
      const scopedId = getActiveInstituteId();
      const headers = { 'Content-Type': 'multipart/form-data' };
      if (scopedId != null) headers['X-Institute-Id'] = String(scopedId);

      const response = await post(endpoint, formData, { headers });
      
      return { 
        data: response?.data || { message: 'File uploaded successfully' }, 
        error: null 
      };
    } catch (error) {
      console.error('Bulk upload error:', error);
      return { 
        data: null, 
        error: error.response?.data?.message || 'Failed to upload file'
      };
    }
  }
};